import { Checkbox, Col, Form, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import CheckboxComponent from 'components/atoms/CheckboxComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputTextArea from 'components/atoms/InputTextArea';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import PrilohyTable from 'components/molecules/Dokumenty/PrilohyTable';
import FormWrapper from 'components/molecules/FormWrapper';

import { IPrikazZmenForm, PrikazZmenFormGet, PrikazZmenFormPut } from 'services/dokumenty/prikazy';
import { ISmlouvniStrany } from 'services/dokumenty/smlouva';

import { EDruhAkcie } from 'enums/isin/udaje/druhAkcie';

import { druhAkciePrikazSelect } from 'data/codebooks/emise/udaje/druhAkcieSelect';
import formaCpSelect from 'data/codebooks/emise/udaje/formaCpSelect';

import { isIsinDlt } from '../NovyDokument';
import { getDataIsins } from './Zruseni';

interface IPrikazForma {
    nextStep: () => void;
    zadostID: number;
    prikazyData: ISmlouvniStrany;
}

const PrikazForma: React.FC<IPrikazForma> = ({ nextStep, zadostID, prikazyData }) => {
    const { dokumenty } = useContext(Context);
    const [form] = Form.useForm();
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    // const [, rerender] = useState<boolean>(false);
    const [data, setData] = useState<IPrikazZmenForm>();
    const [, rerender] = useState<boolean>(false);
    const [dataIsins] = useState<
        {
            labelcz: string;
            value: string;
        }[]
    >(
        getDataIsins(dokumenty).map(e => {
            return {
                labelcz: e.isin,
                value: e.isin
            };
        }) || []
    );
    const [selectedIsin, setSelectIsin] = useState<string>();
    const [isDLT, setIsDLT] = useState<boolean>();
    const [noChangeSelectedErr, setNoChangeSelectedErr] = useState<boolean>(false);

    useEffect(() => {
        if (selectedIsin) {
            setIsDLT(isIsinDlt(selectedIsin, dokumenty!));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIsin]);

    useEffect(() => {
        setLoading(true);
        PrikazZmenFormGet(zadostID)
            .then(resp => {
                setSelectIsin(resp.isin);
                setData(resp);
                form.setFieldsValue({
                    druhp: resp.druhp,
                    druhn: resp.druhn
                });
            })
            .catch(() => setData({} as IPrikazZmenForm))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zadostID]);

    return (
        <LoadingWrapper loading={loading}>
            {data && prikazyData && (
                <>
                    <FormWrapper
                        outForm={form}
                        onFinish={values => {
                            if (!values.formachck && !values.druhchck) setNoChangeSelectedErr(true);
                            else {
                                setLoading(true);
                                const dataToSend = {
                                    ...(values as any),
                                    datum: values.datum.format('YYYY-MM-DD')
                                };
                                delete dataToSend.idmbSpolecnost;
                                delete dataToSend.dicdphSpolecnost;
                                delete dataToSend.nazevSpolecnost;
                                delete dataToSend.leiSpolecnost;
                                delete dataToSend.formachck;
                                delete dataToSend.druhchck;
                                PrikazZmenFormPut(zadostID, dataToSend)
                                    .then(() => nextStep())
                                    .finally(() => setLoading(false));
                            }
                        }}
                    >
                        <Title text="EMITENT" level={5} />
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.idmb}
                            label="IDENTIFIKACNI_CISLO"
                            name="idmbSpolecnost"
                            notRequired={true}
                        />
                        {prikazyData?.spolecnost.dicdph && (
                            <InputComponent
                                disabled={true}
                                initialValue={prikazyData?.spolecnost.dicdph}
                                label="ISIN_IDENTIFIKACE_DIC"
                                name="dicdphSpolecnost"
                                notRequired={true}
                            />
                        )}
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.nazev}
                            label="ISIN_IDENTIFIKACE_FIRMA"
                            name="nazevSpolecnost"
                            notRequired={true}
                        />
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.lei}
                            label="LEI_CELE"
                            name="leiSpolecnost"
                            notRequired={true}
                        />
                        <SelectComponent
                            name="isin"
                            labelParagraph={'PRIKAZ_ZNIZENI_ISIN'}
                            defaultValue={data?.isin}
                            data={dataIsins}
                            isFormItem={true}
                            onChange={val => setSelectIsin(val)}
                        />
                        <Row>
                            <Form.Item
                                style={{
                                    fontWeight: 600
                                }}
                                name="formachck"
                                label={<TranslateText>PRIKAZ_FORMA_TITLE_CHECKBOX</TranslateText>}
                                initialValue={!!data?.formap}
                                valuePropName="checked"
                            >
                                <Checkbox
                                    style={{
                                        position: 'relative',
                                        top: -2,
                                        left: 4
                                    }}
                                    onChange={() => {
                                        rerender(pv => !pv);
                                        setNoChangeSelectedErr(false);
                                    }}
                                />
                            </Form.Item>
                        </Row>
                        <Row>
                            <Form.Item
                                style={{
                                    fontWeight: 600
                                }}
                                name="druhchck"
                                label={<TranslateText>PRIKAZ_FORMA_DRUH_AKCII_CHECKBOX</TranslateText>}
                                initialValue={!!data?.druhp}
                                valuePropName="checked"
                            >
                                <Checkbox
                                    style={{
                                        position: 'relative',
                                        top: -2,
                                        left: 4
                                    }}
                                    onChange={() => {
                                        rerender(pv => !pv);
                                        setNoChangeSelectedErr(false);
                                    }}
                                />
                            </Form.Item>
                        </Row>
                        {form.getFieldValue('formachck') && (
                            <>
                                <Title level={5} text="PRIKAZ_FORMA_TITLE" dividerTitle={true} />
                                <SelectComponent
                                    name="formap"
                                    labelParagraph={'PUVODNI_UDAJ'}
                                    defaultValue={data?.formap}
                                    data={formaCpSelect}
                                    isFormItem={true}
                                />
                                <SelectComponent
                                    name="forman"
                                    labelParagraph={'NOVY_UDAJ'}
                                    defaultValue={data?.forman}
                                    data={formaCpSelect}
                                    isFormItem={true}
                                />{' '}
                            </>
                        )}
                        {form.getFieldValue('druhchck') && (
                            <>
                                <Title level={5} text="PRIKAZ_FORMA_DRUH_AKCII" dividerTitle={true} />
                                <SelectComponent
                                    name="druhp"
                                    labelParagraph={'PUVODNI_UDAJ'}
                                    defaultValue={data?.druhp}
                                    data={druhAkciePrikazSelect}
                                    isFormItem={true}
                                />
                                {form.getFieldValue('druhp') === EDruhAkcie.JINA && (
                                    <InputTextArea
                                        rows={4}
                                        name="zvlpravop"
                                        initialValue={data?.zvlpravop}
                                        label="PRIKAZ_FORMA_DRUH_AKCII_ZVLPRAVO_POPIS"
                                    />
                                )}
                                <SelectComponent
                                    name="druhn"
                                    labelParagraph={'NOVY_UDAJ'}
                                    defaultValue={data?.druhn}
                                    data={druhAkciePrikazSelect}
                                    isFormItem={true}
                                    onChange={() => rerender(pv => !pv)}
                                />
                                {form.getFieldValue('druhn') === EDruhAkcie.JINA && (
                                    <InputTextArea
                                        rows={4}
                                        name="zvlpravon"
                                        initialValue={data?.zvlpravon}
                                        label="PRIKAZ_FORMA_DRUH_AKCII_ZVLPRAVO_POPIS"
                                    />
                                )}
                            </>
                        )}
                        <DatePickerComponent
                            name="datum"
                            initialValue={data?.datum ? dayjs(data.datum) : undefined}
                            //  disabledDate={current => current < dayjs().add(6, 'days')}
                            label="PRIKAZ_PREVOD_DATUM"
                            infoTooltipText="PRIKAZ_ZRUSENI_DATE_TOOLTIP"
                            onChange={val =>
                                form.setFieldsValue({
                                    kedni: val.subtract(1, 'day')
                                })
                            }
                        />
                        <PrilohyTable zadostID={zadostID} required={true} />
                        <Title level={5} text="PRIKAZ_FORMA_PODMINKY_TITLE" />
                        <Paragraph
                            text={isDLT ? 'PRIKAZ_FORMA_PODMINKY_DLT_TEXT1' : 'PRIKAZ_FORMA_PODMINKY_TEXT1'}
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 10
                            }}
                        />

                        <Paragraph
                            text={isDLT ? 'PRIKAZ_FORMA_PODMINKY_DLT_TEXT2' : 'PRIKAZ_FORMA_PODMINKY_TEXT2'}
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 10
                            }}
                        />
                        <Row
                            style={{
                                marginTop: 26
                            }}
                        >
                            <CheckboxComponent
                                name="souhlas"
                                checkboxText={<TranslateText>POTVRZENI_CHECKBOX</TranslateText>}
                                onChange={() => setIsChecked(pv => !pv)}
                                checked={isChecked}
                            />
                        </Row>
                        <Row className="button-row">
                            <Col>
                                <ButtonComponent
                                    buttontext="POKRACOVAT"
                                    id="isinPokracovatBtn"
                                    disabled={!isChecked}
                                    extramessage={noChangeSelectedErr ? 'PRIKAZ_FORMA_CHCKBX_ERR' : ''}
                                    htmlType="submit"
                                />
                            </Col>
                        </Row>
                    </FormWrapper>
                </>
            )}
        </LoadingWrapper>
    );
};

export default PrikazForma;
